import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps
} from '@mui/material'
import React, { FC, useCallback, useState } from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { labelMap } from '../../models/ServiceItemStatuses'
import { useUpdateServiceMutation } from '../../redux/api'
import { ServiceEntry, ServiceInfoStatusEnum } from '../../sdk'
import { ModifyObjectType } from '../../utils'

type Props = ModifyObjectType<
  SelectProps,
  {
    defaultValues: ServiceEntry
    disabled: boolean
    setDialogOpen?: (status: boolean) => void
  }
>

export const StatusSelect: FC<Props> = ({
  defaultValues,
  disabled,
  setDialogOpen
}) => {
  const [updateService] = useUpdateServiceMutation()
  const { accessToken } = useAccessTokenAndShopId()

  const [open, setOpen] = useState(false)

  const onChange = useCallback(
    (event: SelectChangeEvent) => {
      event.stopPropagation()

      if (event.target.value === ServiceInfoStatusEnum.Authorized) {
        setDialogOpen?.(true)
      } else {
        updateService({
          authorization: accessToken,
          serviceEntry: {
            id: defaultValues.id!,
            info: {
              ...defaultValues.info!,
              status: event.target.value as ServiceInfoStatusEnum
            }
          }
        })
      }
    },
    [
      accessToken,
      defaultValues.id,
      defaultValues.info,
      setDialogOpen,
      updateService
    ]
  )

  return (
    <FormControl fullWidth variant="standard" size="small">
      <InputLabel id="statusLabel">Status</InputLabel>
      <Select
        labelId="statusLabel"
        label="Status"
        data-testid="ServiceStatusSelect"
        value={
          defaultValues.info!.status !== ServiceInfoStatusEnum.Authorized &&
          defaultValues.info!.status !== ServiceInfoStatusEnum.Deferred
            ? ServiceInfoStatusEnum.Recommended
            : defaultValues.info!.status
        }
        onChange={onChange}
        open={open}
        disabled={disabled}
        onClose={(event) => {
          event.stopPropagation()
          setOpen(false)
        }}
        onOpen={(event) => {
          event.stopPropagation()
          setOpen(true)
        }}>
        <MenuItem
          value={ServiceInfoStatusEnum.Recommended}
          data-testid={`${ServiceInfoStatusEnum.Recommended}ServiceStatus`}>
          {labelMap[ServiceInfoStatusEnum.Recommended]}
        </MenuItem>
        {setDialogOpen ? (
          <MenuItem
            value={ServiceInfoStatusEnum.Authorized}
            data-testid={`${ServiceInfoStatusEnum.Authorized}ServiceStatus`}>
            {labelMap[ServiceInfoStatusEnum.Authorized]}
          </MenuItem>
        ) : (
          <MenuItem
            disabled
            value={ServiceInfoStatusEnum.Authorized}
            data-testid={`${ServiceInfoStatusEnum.Authorized}ServiceStatus`}>
            {labelMap[ServiceInfoStatusEnum.Authorized]}
          </MenuItem>
        )}
        <MenuItem
          value={ServiceInfoStatusEnum.Deferred}
          data-testid={`${ServiceInfoStatusEnum.Deferred}ServiceStatus`}>
          {labelMap[ServiceInfoStatusEnum.Deferred]}
        </MenuItem>
      </Select>
    </FormControl>
  )
}
